
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eye, Send, User } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/currency';

interface EmailPreviewProps {
  subject: string;
  body: string;
  recipients: any[];
  campaignId?: string | null;
  onClose: () => void;
}

const EmailPreview: React.FC<EmailPreviewProps> = ({
  subject,
  body,
  recipients,
  campaignId,
  onClose
}) => {
  const { userCountry } = useAuth();
  const [selectedRecipient, setSelectedRecipient] = useState(recipients[0]?.id || '');
  // Enriched recipients: influencers get their latest campaign data merged in
  const [enrichedMap, setEnrichedMap] = useState<Record<string, any>>({});

  // Fetch campaign data for influencer recipients for the preview
  useEffect(() => {
    const influencerRecipients = recipients.filter(r => r.type === 'influencer');
    if (influencerRecipients.length === 0) return;

    Promise.all(
      influencerRecipients.map(async (r) => {
        // Determine campaign context: prefer recipient-level campaign_id, then prop-level campaignId
        const effectiveCampaignId = r.campaign_id || campaignId;

        let query = supabase
          .from('campaign_influencers')
          .select(`
            deliverables, fee_committed, fee_actual, status, deadline, bonus,
            campaigns ( name, status, total_budget, start_date, end_date ),
            payments ( due_date )
          `)
          .eq('influencer_id', r.id);

        // If a specific campaign is known, scope to it; otherwise fall back to the latest assignment
        if (effectiveCampaignId) {
          query = query.eq('campaign_id', effectiveCampaignId);
        } else {
          query = query.order('id', { ascending: false });
        }

        const { data, error: ciErr } = await query.limit(1).maybeSingle();

        if (ciErr) console.warn('Campaign fetch for preview failed:', ciErr.message);

        if (!data) return { id: r.id, extra: {} };
        const campaign = (data as any).campaigns;
        return {
          id: r.id,
          extra: {
            deliverables:        data.deliverables,
            fee_committed:       data.fee_committed,
            fee_actual:          data.fee_actual,
            campaign_status:     data.status,
            deadline:            data.deadline,
            bonus:               data.bonus,
            payment_due_date:    data.payments && data.payments.length > 0 ? data.payments[0].due_date : data.deadline,
            campaign_name:       campaign?.name,
            campaign_budget:     campaign?.total_budget,
            campaign_start_date: campaign?.start_date,
            campaign_end_date:   campaign?.end_date,
          },
        };
      })
    ).then((results) => {
      const map: Record<string, any> = {};
      results.forEach(({ id, extra }) => { map[id] = extra; });
      setEnrichedMap(map);
    });
  }, [recipients, campaignId]);

  const personalizeText = (text: string, recipient: any) => {
    if (!recipient) return text;
    
    let personalizedText = text;

    // Derive first_name from full name if not already present
    const firstName = recipient.first_name || (recipient.name ? recipient.name.split(' ')[0] : '');

    const replacements: Record<string, string> = {
      // Personal info
      'name':             recipient.name || '[Name]',
      'first_name':       firstName || '[First Name]',
      'email':            recipient.email || '[Email]',
      'phone':            recipient.phone || recipient.phone_number || '[Phone No.]',
      'handle':           recipient.handle ? `@${recipient.handle}` : '[Handle]',
      'location':         recipient.location || '[Location]',
      'gender':           recipient.gender || '[Gender]',
      // Professional
      'company_name':     recipient.company_name || '[Company]',
      'designation':      recipient.designation || '[Designation]',
      'role':             recipient.role || '[Role]',
      'brand_name':       recipient.brand_name || '[Brand Name]',
      'website':          recipient.website || '[Website]',
      'industry':         recipient.industry || '[Industry]',
      // Influencer metrics
      'followers_count':  recipient.followers_count || recipient.followers || '[Followers Count]',
      'engagement_rate':  recipient.engagement_rate || (recipient.engagement ? `${recipient.engagement}%` : '[Engagement Rate]'),
      'platform':         recipient.platform || '[Platform]',
      'categories':       Array.isArray(recipient.categories) ? recipient.categories.join(', ') : (recipient.categories || '[Categories]'),
      'rate_per_post':    recipient.rate_per_post != null ? formatCurrency(recipient.rate_per_post, userCountry) : (recipient.avgFee != null ? formatCurrency(recipient.avgFee, userCountry) : '[Rate Per Post]'),
      'verified_status':  recipient.isVerified ? 'Verified' : (recipient.verified_status || '[Verification Status]'),
      'languages':        Array.isArray(recipient.languages) ? recipient.languages.join(', ') : (recipient.languages || '[Languages]'),
      // Campaign info — shown as placeholder (not available without campaign context in preview)
      'campaign_name':        recipient.campaign_name || '[Campaign Name]',
      'campaign_status':      recipient.campaign_status || '[Campaign Status]',
      'campaign_budget':      recipient.campaign_budget != null ? formatCurrency(recipient.campaign_budget, userCountry) : '[Campaign Budget]',
      'campaign_start_date':  recipient.campaign_start_date || '[Campaign Start Date]',
      'campaign_end_date':    recipient.campaign_end_date || '[Campaign End Date]',
      'deliverables':         recipient.deliverables || '[Deliverables]',
      'deadline':             recipient.deadline || '[Deadline]',
      // Payment & Contract
      'fee_committed':        recipient.fee_committed != null ? formatCurrency(recipient.fee_committed, userCountry) : '[Committed Fee]',
      'fee_actual':           recipient.fee_actual != null ? formatCurrency(recipient.fee_actual, userCountry) : '[Actual Fee]',
      'payment_status':       recipient.payment_status || '[Payment Status]',
      'contract_status':      recipient.contract_status || '[Contract Status]',
      'payment_due_date':     recipient.payment_due_date || recipient.deadline || '[Payment Due Date]',
      'bonus':                recipient.bonus != null ? formatCurrency(recipient.bonus, userCountry) : '[Bonus Amount]',
      // Contact details
      'contact_name':         recipient.contact_name || '[Contact Name]',
      'contact_email':        recipient.contact_email || '[Contact Email]',
      'contact_phone':        recipient.contact_phone || '[Contact Phone]',
      'contact_role':         recipient.contact_role || '[Contact Role]',
    };

    Object.entries(replacements).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      personalizedText = personalizedText.replace(regex, String(value));
    });

    return personalizedText;
  };


  const rawRecipient = recipients.find(r => r.id === selectedRecipient);
  // Merge any enriched campaign data fetched from DB
  const currentRecipient = rawRecipient
    ? { ...rawRecipient, ...(enrichedMap[rawRecipient.id] || {}) }
    : undefined;
  const personalizedSubject = personalizeText(subject, currentRecipient);
  const personalizedBody = personalizeText(body, currentRecipient);

  // Don't show the dialog if there are no recipients
  if (!recipients || recipients.length === 0) {
    return null;
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Email Preview
          </DialogTitle>
          <DialogDescription>
            Preview how your email will look for different recipients
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Recipient Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Preview for:</label>
            <Select value={selectedRecipient} onValueChange={setSelectedRecipient}>
              <SelectTrigger>
                <SelectValue placeholder="Select a recipient to preview" />
              </SelectTrigger>
              <SelectContent>
                {recipients.map((recipient) => (
                  <SelectItem key={recipient.id} value={recipient.id}>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{recipient.name}</span>
                      <Badge variant="outline" className="ml-2">
                        {recipient.type}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Email Preview */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Email Preview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Email Headers */}
              <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">To:</span>
                  <span className="text-sm">{currentRecipient?.email || '[Email]'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Subject:</span>
                  <span className="text-sm font-medium">{personalizedSubject}</span>
                </div>
              </div>

              {/* Email Body */}
              <div className="border rounded-lg p-6 bg-white">
                <div className="prose prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">
                    {personalizedBody}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Campaign data warning for influencers with no campaign */}
          {currentRecipient?.type === 'influencer' && !enrichedMap[currentRecipient.id]?.campaign_name && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              ⚠️ <strong>{currentRecipient.name}</strong> is not assigned to any campaign yet.
              Campaign variables (campaign_name, fee_committed, etc.) will show as placeholders until they are added to a campaign.
            </div>
          )}

          {/* Recipient Info */}
          {currentRecipient && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Recipient Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">Name:</span>
                    <span className="ml-2">{currentRecipient.name}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Type:</span>
                    <Badge variant="outline" className="ml-2">
                      {currentRecipient.type}
                    </Badge>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Email:</span>
                    <span className="ml-2">{currentRecipient.email}</span>
                  </div>
                  {currentRecipient.company_name && (
                    <div>
                      <span className="font-medium text-gray-600">Company:</span>
                      <span className="ml-2">{currentRecipient.company_name}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Close Preview
            </Button>
            <Button className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              Send Bulk Email
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreview;
